/**
 * What the reviewer has done to each MA brand THIS session.
 *
 * Same lifecycle as `sessionCache.ts` and module-level for the same reason: the
 * write that flips a row to "created"/"updated" completes while the Curated
 * Watches tab is mounted and this tab is not. Lost on tab close — the next
 * session rebuilds "already imported" from the WK brand's own
 * `externalRefs.microbrandAtlas` stamp at match time.
 */
import { useSyncExternalStore } from 'react';
import { Badge } from '../common/Badge';

export type SessionStatusKind = 'created' | 'updated' | 'mapped' | 'dismissed';

export interface SessionStatus {
  kind: SessionStatusKind;
  /** The WK brand written to (created/updated) or manually chosen (mapped). */
  wkBrandId?: string;
}

interface SessionStatusState {
  statuses: ReadonlyMap<string, SessionStatus>;
  /**
   * MA slugs whose AUTO-match the reviewer rejected ("not the same brand").
   * Not a status: the row simply goes back to unmatched, with Create offered.
   */
  rejectedMatches: ReadonlySet<string>;
}

let state: SessionStatusState = {
  statuses: new Map(),
  rejectedMatches: new Set(),
};

const listeners = new Set<() => void>();

function commit(next: SessionStatusState) {
  state = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getSessionStatus(maSlug: string): SessionStatus | undefined {
  return state.statuses.get(maSlug);
}

export function setSessionStatus(maSlug: string, status: SessionStatus) {
  const statuses = new Map(state.statuses);
  statuses.set(maSlug, status);
  // A deliberate action on a row supersedes an earlier "not a match".
  const rejectedMatches = new Set(state.rejectedMatches);
  rejectedMatches.delete(maSlug);
  commit({ statuses, rejectedMatches });
}

export function clearSessionStatus(maSlug: string) {
  if (!state.statuses.has(maSlug)) return;
  const statuses = new Map(state.statuses);
  statuses.delete(maSlug);
  commit({ ...state, statuses });
}

/** Reject the auto-match for a row, dropping any manual mapping with it. */
export function rejectAutoMatch(maSlug: string) {
  const rejectedMatches = new Set(state.rejectedMatches);
  rejectedMatches.add(maSlug);
  const statuses = new Map(state.statuses);
  if (statuses.get(maSlug)?.kind === 'mapped') statuses.delete(maSlug);
  commit({ statuses, rejectedMatches });
}

export function restoreAutoMatch(maSlug: string) {
  if (!state.rejectedMatches.has(maSlug)) return;
  const rejectedMatches = new Set(state.rejectedMatches);
  rejectedMatches.delete(maSlug);
  commit({ ...state, rejectedMatches });
}

export function useSessionStatuses(): SessionStatusState {
  return useSyncExternalStore(subscribe, () => state);
}

/** Test seam. */
export function resetSessionStatusForTests() {
  commit({ statuses: new Map(), rejectedMatches: new Set() });
}

const LABELS: Record<SessionStatusKind, string> = {
  created: 'Created this session',
  updated: 'Updated this session',
  mapped: 'Mapped this session',
  dismissed: 'Dismissed',
};

const VARIANTS: Record<SessionStatusKind, 'success' | 'info' | 'amber' | 'default'> = {
  created: 'success',
  updated: 'info',
  mapped: 'amber',
  dismissed: 'default',
};

export function SessionStatusBadge({ status }: { status: SessionStatus | undefined }) {
  if (!status) return null;
  return <Badge variant={VARIANTS[status.kind]}>{LABELS[status.kind]}</Badge>;
}
